import { useEffect, useState } from 'react';
import { Event } from 'nostr-tools';
import { useAdmin } from '../contexts/AdminContext';
import { getNostrPool } from '../lib/nostrPool';
import { RELAYS } from '../config/constants';
import { generateSlug } from '../lib/urlUtils';
import { useGlobalModeration } from './useGlobalModeration';

export interface NostrTopic {
    id: string;
    title: string;
    slug: string;
    entry_count: number;
    last_entry_at: number;
}

export const useNostrTopics = () => {
    const [topics, setTopics] = useState<NostrTopic[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const pool = getNostrPool(); // Use singleton
    const { settings } = useAdmin();
    const { isolateCommunity, deletedEventIds, bannedWords } = settings;
    const { blockedEventIds } = useGlobalModeration();

    useEffect(() => {
        setIsLoading(true);
        setTopics([]); // Clear existing topics to prevent duplicate counting on re-subscription
        const seenEvents = new Set<string>();

        const filter: any = {
            kinds: [1],
            limit: 100,
        };

        if (isolateCommunity) {
            filter['#t'] = ['mavililer'];
        }

        const sub = pool.subscribeMany(RELAYS, filter, {
            onevent(event: Event) {
                if (seenEvents.has(event.id)) return;

                // Filter deleted and blocked events
                if (deletedEventIds.includes(event.id)) return;
                if (blockedEventIds.has(event.id)) return;

                // Filter banned words locally
                if (bannedWords.some(word => event.content.toLowerCase().includes(word.toLowerCase()))) return;

                seenEvents.add(event.id);

                // Get subject tag for title (original, not slugified)
                const subjectTag = event.tags.find(t => t[0] === 'subject')?.[1]

                // Get only the FIRST topic tag (the actual topic, not hashtags)
                // Tags structure: ['t', topic-slug], ['t', 'mavililer'], ['t', 'mavililersözlük'], etc.
                // We want ONLY the first one which is the actual topic
                const topicTag = event.tags.find(t =>
                    t[0] === 't' &&
                    t[1] !== 'mavililer' &&
                    t[1] !== 'mavililersözlük' &&
                    t[1] !== 'nostr' &&
                    t[1] !== 'türkiye'
                )?.[1];

                // If no valid topic found, skip this event
                if (!topicTag) return;

                setTopics((prev) => {
                    const topicsMap = new Map();
                    // Initialize with previous items to maintain entries
                    prev.forEach(t => topicsMap.set(t.slug, { ...t }));

                    // Process only the ONE topic tag (not all hashtags)
                    const title = subjectTag || topicTag.replace(/-/g, ' ')
                    // Use standard generateSlug to ensure consistency with EntryCard
                    const slug = generateSlug(title)

                    if (topicsMap.has(slug)) {
                        const existing = topicsMap.get(slug)!;
                        topicsMap.set(slug, {
                            ...existing,
                            entry_count: existing.entry_count + 1,
                            last_entry_at: Math.max(existing.last_entry_at, event.created_at)
                        });
                    } else {
                        topicsMap.set(slug, {
                            id: title,
                            title: title,
                            slug: slug,
                            entry_count: 1,
                            last_entry_at: event.created_at
                        });
                    }

                    return Array.from(topicsMap.values())
                        .filter(t => t.entry_count > 0) // Hide empty topics
                        .sort((a, b) => b.entry_count - a.entry_count)
                        .slice(0, 50); // Increased limit for better filtering
                });
            },
            oneose() {
                setIsLoading(false);
            },
        });

        // Listen for entry deletions to update counts
        const handleEntryDeleted = (e: CustomEvent) => {
            const { topicSlug } = e.detail
            if (!topicSlug) return

            setTopics((prev) => {
                // First map to decrement, then filter
                const updated = prev.map(t => {
                    if (t.slug === topicSlug || t.slug === generateSlug(topicSlug)) { // Check both exact and generated matches
                        return { ...t, entry_count: Math.max(0, t.entry_count - 1) }
                    }
                    return t
                })

                return updated.filter(t => t.entry_count > 0)
            })
        }

        window.addEventListener('entry-deleted', handleEntryDeleted as EventListener)

        return () => {
            sub.close();
            window.removeEventListener('entry-deleted', handleEntryDeleted as EventListener)
        };
    }, [isolateCommunity, deletedEventIds, bannedWords, blockedEventIds]);

    return { topics, isLoading };
};
